import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Loader2, CreditCard, Zap, Megaphone, RefreshCw, CalendarClock } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useWorkspaceId } from '@/contexts/WorkspaceContext';
import PlanSelector, { PublicPlan } from '@/components/PlanSelector';
import ChangePlanDialog from '@/components/ChangePlanDialog';
import { billingIntervalLabel } from '@/lib/plans';
import { formatCredits } from '@/lib/creditCosts';

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  category: string;
  billingReason: string | null;
  creditsPurchased: number;
  createdAt: string;
}

interface BillingData {
  plan: { key: string; name: string; price: number | null; currency: string; interval: string; topUpRate: number | null; topUpMax: number | null } | null;
  availablePlan: PublicPlan | null;
  pendingPlan: { key: string; name: string; effectiveAt: string | null } | null;
  subscriptionStatus: string;
  cancelAtPeriodEnd: boolean;
  creditLimit: number;
  creditsRemaining: number;
  topUpCreditsRemaining: number;
  campaignsUsed: number;
  campaignsLimit: number | null;
  planRenewsAt: string | null;
  recentPayments: Payment[];
}


// Top-up slider bounds. The upper bound comes from the plan's `topUpMax`; this is only
// the fallback for a plan that enables top-ups without setting a cap.
const TOP_UP_MIN = 100;
const TOP_UP_STEP = 100;
const TOP_UP_FALLBACK_MAX = 100000;

const STATUS_VARIANT: Record<string, string> = {
  active: 'bg-green-500/15 text-green-600',
  trialing: 'bg-blue-500/15 text-blue-600',
  past_due: 'bg-amber-500/15 text-amber-600',
  canceled: 'bg-red-500/15 text-red-600',
  none: 'bg-muted text-muted-foreground',
};

export default function Billing() {
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [topUpCredits, setTopUpCredits] = useState(1000);
  const [buying, setBuying] = useState(false);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [cancelingChange, setCancelingChange] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<BillingData>(`/workspaces/${workspaceId}/billing`)
      .then(setData)
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Failed to load billing'))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // Show any toast stashed before a reload (e.g. an upgrade confirmation).
  useEffect(() => {
    const msg = sessionStorage.getItem('billingToast');
    if (msg) {
      sessionStorage.removeItem('billingToast');
      toast.success(msg);
    }
  }, []);

  // Keep the chosen top-up amount within the remaining headroom once billing data loads.
  // Headroom = plan ceiling − the top-up credits the workspace already holds (cumulative cap).
  useEffect(() => {
    if (!data?.plan) return;
    const cap = data.plan.topUpMax ?? TOP_UP_FALLBACK_MAX;
    const headroom = Math.max(0, cap - (data.topUpCreditsRemaining || 0));
    setTopUpCredits((c) => Math.min(Math.max(c || TOP_UP_MIN, TOP_UP_MIN), Math.max(headroom, TOP_UP_MIN)));
  }, [data]);

  // Notice when the user returns from a canceled Stripe checkout (?checkout=cancelled).
  useEffect(() => {
    if (searchParams.get('checkout') === 'cancelled') {
      toast.info('Checkout canceled — no changes were made.');
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      const res = await apiFetch<{ url: string }>(`/workspaces/${workspaceId}/billing/portal`, { method: 'POST' });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not open billing portal');
      setPortalLoading(false);
    }
  };

  const buyCredits = async (credits: number) => {
    try {
      setBuying(true);
      const res = await apiFetch<{ checkoutUrl: string }>(`/workspaces/${workspaceId}/billing/top-up`, {
        method: 'POST',
        body: JSON.stringify({ credits }),
      });
      window.location.href = res.checkoutUrl;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start top-up');
      setBuying(false);
    }
  };

  // Result of an in-app plan change. An upgrade alters credits/entitlements now, so
  // reload to refresh app-wide state (Navbar credits, feature gates). A scheduled
  // downgrade or a canceled change touches nothing global — just refetch billing.
  const handleChanged = (res: { effect: string; chargedAmount?: number; currency?: string }) => {
    setChangeOpen(false);
    if (res.effect === 'upgraded') {
      const charged = res.chargedAmount && res.chargedAmount > 0
        ? ` You were charged ${(res.currency || '').toUpperCase()} ${(res.chargedAmount / 100).toLocaleString()} for the prorated difference.`
        : '';
      // The reload below refreshes app-wide state (Navbar credits, entitlements);
      // stash the toast so it survives the reload and is shown on mount.
      sessionStorage.setItem('billingToast', `Your plan has been upgraded.${charged}`);
      window.location.reload();
      return;
    }
    if (res.effect === 'downgrade_scheduled') {
      toast.success('Your downgrade is scheduled for the end of this billing period.');
    } else {
      toast.success('Your scheduled change was canceled.');
    }
    load();
  };

  const cancelScheduledChange = async () => {
    try {
      setCancelingChange(true);
      await apiFetch(`/workspaces/${workspaceId}/billing/cancel-scheduled-change`, { method: 'POST' });
      toast.success('Scheduled change canceled — you’ll stay on your current plan.');
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not cancel the scheduled change');
    } finally {
      setCancelingChange(false);
    }
  };

  const subscribe = async (plan: PublicPlan) => {
    if (plan.price == null) { navigate('/support'); return; }
    setSubscribing(plan.key);
    try {
      const res = await apiFetch<{ checkoutUrl: string }>(`/workspaces/${workspaceId}/billing/checkout`, {
        method: 'POST',
        body: JSON.stringify({ planKey: plan.key, topUpCredits: 0, acceptTerms: true }),
      });
      window.location.href = res.checkoutUrl;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start checkout');
      setSubscribing(null);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
    }
    if (!data) return null;

    const subscriptionActive = data.subscriptionStatus === 'active' || data.subscriptionStatus === 'trialing';

    // Not subscribed yet -> show the selectable plans, centered.
    if (!subscriptionActive) {
      const customPlan = data.availablePlan;
      return (
        <div className="container mx-auto max-w-7xl space-y-10">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              {customPlan ? 'Your enterprise plan is ready' : 'Choose a plan to unlock your workspace'}
            </h1>
            <p className="text-muted-foreground">
              {customPlan
                ? 'Subscribe to your custom plan to unlock your workspace, or pick one of our standard plans below.'
                : 'Subscribe to start creating campaigns, importing contacts, and generating AI content. Change or cancel anytime.'}
            </p>
          </div>
          <PlanSelector
            busyKey={subscribing}
            ctaLabel="Subscribe"
            columns={4}
            extraPlans={customPlan ? [customPlan] : undefined}
            highlightKey={customPlan?.key}
            onSubscribe={subscribe}
          />
        </div>
      );
    }

    const planCredits = Math.max(0, (data.creditsRemaining || 0) - (data.topUpCreditsRemaining || 0));
    const planPct = data.creditLimit > 0 ? Math.round((planCredits / data.creditLimit) * 100) : 0;
    const statusClass = STATUS_VARIANT[data.subscriptionStatus] || STATUS_VARIANT.none;
    const topUpRate = data.plan?.topUpRate || 0;
    const topUpCeiling = data.plan?.topUpMax ?? TOP_UP_FALLBACK_MAX;
    const topUpBalance = data.topUpCreditsRemaining || 0;
    const topUpHeadroom = Math.max(0, topUpCeiling - topUpBalance);
    const hasTopUpRoom = topUpHeadroom >= TOP_UP_MIN;
    const sliderMax = Math.max(topUpHeadroom, TOP_UP_MIN); // keep max >= min for the Slider
    const sliderValue = Math.min(Math.max(topUpCredits, TOP_UP_MIN), sliderMax);
    const capLabel = data.plan?.topUpMax != null ? data.plan.topUpMax.toLocaleString() : null;
    const topUpPct = data.plan?.topUpMax ? Math.min(100, Math.round((topUpBalance / data.plan.topUpMax) * 100)) : 0;
    const campaignsUsed = data.campaignsUsed || 0;
    const campaignsLimit = data.campaignsLimit; // null = unlimited
    const campaignsPct = campaignsLimit && campaignsLimit > 0
      ? Math.min(100, Math.round((campaignsUsed / campaignsLimit) * 100))
      : 0;

    return (
      <div className="container mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold">Billing &amp; Subscription</h1>
          <Badge className={statusClass}>{data.subscriptionStatus}</Badge>
        </div>

        <Card className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Current plan</p>
              <p className="text-xl font-semibold">{data.plan?.name || 'No active plan'}</p>
              {data.plan?.price != null && (
                <p className="text-sm text-muted-foreground">
                  {data.plan.currency?.toUpperCase()} {data.plan.price.toLocaleString()} / {billingIntervalLabel(data.plan.interval)}
                </p>
              )}
              {data.planRenewsAt && (
                <p className="text-sm text-muted-foreground mt-1">
                  {data.cancelAtPeriodEnd ? 'Cancels on ' : 'Renews on '}
                  {new Date(data.planRenewsAt).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setChangeOpen(true)}>
                <RefreshCw className="h-4 w-4 mr-2" />Change plan
              </Button>
              <Button variant="outline" onClick={openPortal} disabled={portalLoading}>
                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" />Manage subscription</>}
              </Button>
            </div>
          </div>

          {data.pendingPlan && (
            <div className="mt-4 flex items-start justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
              <div className="flex items-start gap-2">
                <CalendarClock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm">
                  Scheduled to switch to <span className="font-medium">{data.pendingPlan.name}</span>
                  {data.pendingPlan.effectiveAt ? <> on {new Date(data.pendingPlan.effectiveAt).toLocaleDateString()}</> : ' at the end of your billing period'}.
                  You keep your current plan until then.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={cancelScheduledChange} disabled={cancelingChange}>
                {cancelingChange ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel change'}
              </Button>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">Plan credits</p>
            <p className="text-lg font-semibold">{formatCredits(planCredits)} / {formatCredits(data.creditLimit)}</p>
          </div>
          <Progress value={planPct} className="h-2" />
          <p className="text-sm text-muted-foreground mt-3">Resets to your plan allotment each billing cycle.</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Campaigns</p>
              <p className="text-lg font-semibold">
                {campaignsUsed.toLocaleString()}
                {campaignsLimit != null ? ` / ${campaignsLimit.toLocaleString()}` : ' / Unlimited'}
              </p>
            </div>
            <Megaphone className="h-5 w-5 text-muted-foreground" />
          </div>
          {campaignsLimit != null && <Progress value={campaignsPct} className="h-2" />}
          <p className="text-sm text-muted-foreground mt-3">
            Campaigns created on this workspace{campaignsLimit != null ? ' count toward your plan limit.' : '. Your plan has no campaign limit.'}
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Top-up credits</p>
              <p className="text-lg font-semibold">
                {formatCredits(topUpBalance)}{capLabel ? ` / ${capLabel}` : ''}
              </p>
            </div>
            <Zap className="h-5 w-5 text-muted-foreground" />
          </div>
          {capLabel && <Progress value={topUpPct} className="h-2" />}
          <p className="text-sm text-muted-foreground mt-3">
            Extra credits on top of your plan — they never expire and carry over each month.
          </p>

          <div className="mt-6 border-t pt-6">
            {!topUpRate ? (
              <p className="text-sm text-muted-foreground">Top-ups aren’t available on your current plan.</p>
            ) : !hasTopUpRoom ? (
              <p className="text-sm text-muted-foreground">
                You’ve reached your plan’s top-up limit{capLabel ? ` of ${capLabel} credits` : ''}. Spend some
                credits or upgrade your plan to buy more.
              </p>
            ) : (
              <div className="space-y-5">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Buy more credits</p>
                    <p className="text-3xl font-bold">{sliderValue.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total cost</p>
                    <p className="text-lg font-semibold">
                      {data.plan?.currency?.toUpperCase()} {(sliderValue * topUpRate).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Slider
                    value={[sliderValue]}
                    min={TOP_UP_MIN}
                    max={sliderMax}
                    step={TOP_UP_STEP}
                    onValueChange={([v]) => setTopUpCredits(v)}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{TOP_UP_MIN.toLocaleString()}</span>
                    <span>Max {sliderMax.toLocaleString()}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.plan?.currency?.toUpperCase()} {topUpRate.toLocaleString()} per credit. Top-up credits never expire.
                </p>
                <Button className="w-full" onClick={() => buyCredits(sliderValue)} disabled={buying || sliderValue <= 0}>
                  {buying ? 'Redirecting…' : <><Zap className="h-4 w-4 mr-2" />Buy credits</>}
                </Button>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold mb-4">Payment history</h2>
          {data.recentPayments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payments yet.</p>
          ) : (
            <div className="divide-y">
              {data.recentPayments.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <span className="font-medium">{p.billingReason || p.category}</span>
                    <span className="text-muted-foreground"> · {new Date(p.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span>{p.currency?.toUpperCase()} {(p.amount / 100).toLocaleString()}</span>
                    <Badge variant={p.status === 'completed' ? 'default' : 'secondary'}>{p.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <ChangePlanDialog
          open={changeOpen}
          onOpenChange={setChangeOpen}
          workspaceId={workspaceId}
          currentPlan={data.plan ? { key: data.plan.key, name: data.plan.name, price: data.plan.price, interval: data.plan.interval } : null}
          pendingPlanKey={data.pendingPlan?.key ?? null}
          renewsAt={data.planRenewsAt}
          availablePlan={data.availablePlan}
          onChanged={handleChanged}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 py-10 px-4">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
}
